import React, { useState } from 'react';
import { UserProfile, Badge, Challenge, Reward } from '../../../app/domain/types/index.ts';
import { Trophy, Zap, Star, Target, Gift, Lock, CheckCircle, Flame, Shield, Award, ChevronRight } from 'lucide-react';

interface GamificationProps {
  user: UserProfile;
}

const Gamification: React.FC<GamificationProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'badges' | 'rewards'>('overview');

  // Mock Data
  const badges: Badge[] = [
    { id: '1', name: 'First Step', description: 'Complete your profile setup', icon: '🦶', category: 'milestone', earnedDate: '2023-11-01', isLocked: false, rarity: 'common' },
    { id: '2', name: 'Hydration Hero', description: 'Log water intake for 7 days', icon: '💧', category: 'health', earnedDate: '2023-11-10', isLocked: false, rarity: 'common' },
    { id: '3', name: 'Community Pillar', description: 'Get 50 helpful votes', icon: '🏛️', category: 'community', isLocked: true, rarity: 'epic' },
    { id: '4', name: 'Weight Warrior', description: 'Lose 5% body weight', icon: '⚖️', category: 'health', isLocked: true, rarity: 'rare' },
    { id: '5', name: 'Early Bird', description: 'Complete a workout before 7am', icon: '🌅', category: 'health', earnedDate: '2023-11-15', isLocked: false, rarity: 'common' },
    { id: '6', name: 'Legendary Helper', description: 'Reach Platinum Rank', icon: '👑', category: 'community', isLocked: true, rarity: 'legendary' },
  ];

  const challenges: Challenge[] = [
    { id: '1', title: 'Walk the Walk', description: 'Walk 10,000 steps today', progress: 6500, total: 10000, daysLeft: 1, rewardPoints: 50, type: 'daily' },
    { id: '2', title: 'Veggie Variety', description: 'Eat 5 different vegetables this week', progress: 3, total: 5, daysLeft: 3, rewardPoints: 150, type: 'weekly' },
    { id: '3', title: 'Community Voice', description: 'Reply to 3 discussions', progress: 1, total: 3, daysLeft: 2, rewardPoints: 75, type: 'weekly' },
  ];

  const rewards: Reward[] = [
    { id: '1', title: '10% Off Consultation', description: 'Get a discount on your next nutritionist visit.', cost: 500, type: 'discount' },
    { id: '2', title: 'Premium Recipes Pack', description: 'Unlock 50+ chef-curated low-glycemic recipes.', cost: 200, type: 'content' },
    { id: '3', title: '20% Off Gym Membership', description: 'Valid at partner gym locations.', cost: 1000, type: 'discount' },
  ];

  const calculateProgress = (curr: number, total: number) => (curr / total) * 100;

  return (
    <div className="p-6 md:p-8 h-full overflow-y-auto no-scrollbar pb-24 bg-[#FAFAFA] dark:bg-[#0f0f0f]">
      
      {/* Header */}
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-gray-800 dark:text-white">Achievements</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
            <Trophy size={16} className="text-yellow-500" />
            Level {user.currentLevel || 12} • <span className="font-medium text-gray-900 dark:text-white">{user.communityPoints} Points</span>
          </p>
        </div>
        
        {/* Streak Counter */}
        <div className="hidden md:flex items-center gap-3 bg-white dark:bg-stone-800 px-4 py-2 rounded-2xl shadow-sm border border-orange-100 dark:border-orange-900/30">
             <div className="bg-orange-50 dark:bg-orange-900/30 p-2 rounded-full text-orange-500">
                <Flame size={20} className={user.streakDays > 0 ? "fill-orange-500 animate-pulse" : ""} />
             </div>
             <div>
                <p className="text-xs text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider">Current Streak</p>
                <p className="text-lg font-bold text-gray-800 dark:text-white">{user.streakDays || 5} Days</p>
             </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-8 border-b border-stone-200 dark:border-stone-800 pb-1">
        {['overview', 'badges', 'rewards'].map((tab) => (
            <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`pb-3 px-2 text-sm font-medium transition-all relative ${
                    activeTab === tab 
                    ? 'text-gray-900 dark:text-white' 
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                }`}
            >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {activeTab === tab && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-health-600 dark:bg-health-500 rounded-t-full"></div>}
            </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-fade-in-up">
            
            {/* Level Card */}
            <div className="lg:col-span-8 glass-panel rounded-3xl p-8 relative overflow-hidden bg-white/60 dark:bg-stone-900/60 border-stone-100 dark:border-stone-700">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-radial from-yellow-100/40 to-transparent blur-3xl -mr-16 -mt-16 pointer-events-none dark:opacity-20"></div>
                
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h2 className="text-2xl font-medium text-gray-900 dark:text-white">Level Progress</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Keep up the good work! You're almost at the next tier.</p>
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-3 py-1 rounded-full text-xs font-bold border border-yellow-100 dark:border-yellow-800 flex items-center gap-1">
                        <Star size={12} fill="currentColor" /> Multiplier x1.2
                    </div>
                </div>

                <div className="mb-2 flex justify-between text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <span>Level {user.currentLevel || 12}</span>
                    <span>Level {(user.currentLevel || 12) + 1}</span>
                </div>
                <div className="relative h-6 bg-stone-100 dark:bg-stone-700 rounded-full overflow-hidden shadow-inner">
                    <div 
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-health-400 to-health-600 transition-all duration-1000 ease-out"
                        style={{ width: `${((user.currentXP || 750) / (user.nextLevelXP || 1000)) * 100}%` }}
                    >
                        <div className="absolute top-0 right-0 h-full w-2 bg-white/30 blur-[2px]"></div>
                    </div>
                </div>
                <div className="mt-2 text-right text-xs text-gray-400">
                    {user.currentXP || 750} / {user.nextLevelXP || 1000} XP
                </div>
            </div>

            {/* Quick Stats */}
            <div className="lg:col-span-4 grid grid-cols-2 gap-4">
                <div className="glass-panel p-5 rounded-2xl flex flex-col justify-center items-center text-center bg-white/60 dark:bg-stone-900/60 border-stone-100 dark:border-stone-700">
                    <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-500 dark:text-blue-400 mb-2">
                        <Shield size={20} />
                    </div>
                    <span className="text-2xl font-light text-gray-900 dark:text-white">{user.communityRank}</span>
                    <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Rank</span>
                </div>
                <div className="glass-panel p-5 rounded-2xl flex flex-col justify-center items-center text-center bg-white/60 dark:bg-stone-900/60 border-stone-100 dark:border-stone-700">
                    <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/30 rounded-full flex items-center justify-center text-purple-500 dark:text-purple-400 mb-2">
                        <Award size={20} />
                    </div>
                    <span className="text-2xl font-light text-gray-900 dark:text-white">12</span>
                    <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Badges</span>
                </div>
            </div>

            {/* Active Challenges */}
            <div className="lg:col-span-12">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Target size={20} className="text-health-600 dark:text-health-400" /> Active Challenges
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {challenges.map((challenge) => (
                        <div key={challenge.id} className="bg-white dark:bg-stone-800 p-5 rounded-2xl border border-stone-100 dark:border-stone-700 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-3">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                                    challenge.type === 'daily' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                                }`}>
                                    {challenge.type}
                                </span>
                                <span className="text-xs font-semibold text-orange-500 flex items-center gap-1">
                                    <Zap size={12} fill="currentColor" /> +{challenge.rewardPoints} pts
                                </span>
                            </div>
                            <h4 className="font-medium text-gray-900 dark:text-white mb-1">{challenge.title}</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 h-8">{challenge.description}</p>
                            
                            <div className="flex justify-between text-xs text-gray-400 mb-1">
                                <span>{Math.round(calculateProgress(challenge.progress, challenge.total))}%</span>
                                <span>{challenge.progress}/{challenge.total}</span>
                            </div>
                            <div className="h-2 bg-stone-100 dark:bg-stone-700 rounded-full overflow-hidden">
                                <div 
                                    className={`h-full rounded-full ${challenge.type === 'daily' ? 'bg-blue-500' : 'bg-purple-500'}`} 
                                    style={{ width: `${calculateProgress(challenge.progress, challenge.total)}%` }}
                                ></div>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-2 text-right">{challenge.daysLeft} days left</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
      )}

      {/* BADGES TAB */}
      {activeTab === 'badges' && (
        <div className="animate-fade-in-up">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                {badges.map((badge) => (
                    <div key={badge.id} className={`flex flex-col items-center p-6 rounded-3xl border transition-all duration-300 group ${
                        badge.isLocked 
                        ? 'bg-stone-50 dark:bg-stone-800/50 border-dashed border-stone-200 dark:border-stone-700 opacity-60 grayscale' 
                        : 'bg-white dark:bg-stone-800 border-solid border-stone-100 dark:border-stone-700 shadow-sm hover:shadow-md hover:scale-105'
                    }`}>
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-4 shadow-sm relative ${
                            badge.isLocked ? 'bg-stone-200 dark:bg-stone-700' : 'bg-gradient-to-br from-white to-stone-50 dark:from-stone-700 dark:to-stone-600 border border-white dark:border-stone-500'
                        }`}>
                            {badge.icon}
                            {!badge.isLocked && (
                                <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white dark:border-stone-800 flex items-center justify-center ${
                                    badge.rarity === 'common' ? 'bg-gray-400' :
                                    badge.rarity === 'rare' ? 'bg-blue-400' :
                                    badge.rarity === 'epic' ? 'bg-purple-500' : 'bg-yellow-500'
                                }`}>
                                    <Star size={10} className="text-white" fill="currentColor" />
                                </div>
                            )}
                        </div>
                        <h4 className="font-semibold text-sm text-gray-900 dark:text-white text-center mb-1">{badge.name}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-tight">{badge.description}</p>
                        {badge.earnedDate && (
                            <span className="mt-3 text-[10px] text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <CheckCircle size={10} /> Earned
                            </span>
                        )}
                        {badge.isLocked && (
                            <span className="mt-3 text-[10px] text-gray-400 flex items-center gap-1">
                                <Lock size={10} /> Locked
                            </span>
                        )}
                    </div>
                ))}
            </div>
        </div>
      )}

      {/* REWARDS TAB */}
      {activeTab === 'rewards' && (
        <div className="animate-fade-in-up">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 dark:from-black dark:to-gray-900 rounded-3xl p-8 text-white mb-8 flex justify-between items-center relative overflow-hidden shadow-lg">
                <div className="relative z-10">
                    <p className="text-gray-400 text-sm uppercase tracking-wider font-semibold mb-1">Available Balance</p>
                    <h2 className="text-4xl font-light">{user.communityPoints} <span className="text-xl text-yellow-500 font-bold">PTS</span></h2>
                </div>
                <div className="absolute right-0 top-0 h-full w-1/2 bg-gradient-to-l from-white/10 to-transparent skew-x-12"></div>
                <Gift size={64} className="text-white/10 relative z-10" />
            </div>

            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Redeem Rewards</h3>
            <div className="space-y-4">
                {rewards.map((reward) => (
                    <div key={reward.id} className="bg-white dark:bg-stone-800 p-5 rounded-2xl border border-stone-100 dark:border-stone-700 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4 hover:border-health-200 dark:hover:border-health-800 transition-colors">
                        <div className="flex items-center gap-4 w-full md:w-auto">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                reward.type === 'discount' ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                            }`}>
                                {reward.type === 'discount' ? <DollarSignIcon size={24} /> : <BookOpenIcon size={24} />}
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900 dark:text-white">{reward.title}</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{reward.description}</p>
                            </div>
                        </div>
                        
                        <button 
                            disabled={user.communityPoints < reward.cost}
                            className={`px-6 py-2.5 rounded-full font-medium text-sm flex items-center gap-2 min-w-[140px] justify-center transition-all ${
                                user.communityPoints >= reward.cost 
                                ? 'bg-health-600 text-white hover:bg-health-700 shadow-lg shadow-health-600/20' 
                                : 'bg-stone-100 dark:bg-stone-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            {user.communityPoints >= reward.cost ? (
                                <>Redeem <ChevronRight size={16} /></>
                            ) : (
                                <>Need {reward.cost - user.communityPoints} pts</>
                            )}
                        </button>
                    </div>
                ))}
            </div>
        </div>
      )}
    </div>
  );
};

// Helper Icons for Rewards
const DollarSignIcon = ({ size }: { size: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
);
const BookOpenIcon = ({ size }: { size: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>
);

export default Gamification;